import { Paginated, Query } from '@feathersjs/feathers';
import { CompanyProp } from '@tymbe/schema/company-blocked-user.interface';
import { PersonData } from '@tymbe/schema/person.interface';
import { getBlocks, isBlocked } from '@tymbe/utils/company-blocked-user';
import { GroupBase, OptionsOrGroups } from 'react-select';

import feathersClient from '../../../../../apiClient';

import TySelectInvitationOption from './TySelectInvitationOption';
import { PersonCategory, TySelectInvitationOptionData, TySelectInvitationQueryConfig } from './TySelectInvitationTypes';
import { ErrorAlert } from '../../../../../components/alerts';
import { JSX } from 'react';

// Query builder for requests to Persons and their application data.
// Purpose of this builder is to provide one flexible query that is then used
// in the respective calls to different categories of person data.
export const queryBuilder = (
  queryConfig: TySelectInvitationQueryConfig,
  modifiers: { [key: string]: number | boolean },
  eager: string,
  excludeIds: number[],
  limit?: number,
  search?: string,
): Query => {
  const isSearchAnId = search && !Number.isNaN(Number(search));

  const query: Query = {
    $skip: 0,
    $eager: eager,
    $modify: {
      onlyRole: [queryConfig.filter?.role ? queryConfig.filter.role : 'tymber'],
      ...modifiers,
    },
    $limit: limit || 100,
    $limitToRelatedCompanies: false,
    $and: [],
  };

  if (queryConfig.limitToRelatedCompanies) {
    query.$limitToRelatedCompanies = true;
  }

  if (isSearchAnId) {
    query.$and = [{
      'person.id': search,
    }];
  } else {
    query.$modify = {
      ...query.$modify,
      searchName: [search],
    };
  }

  if (queryConfig.onlyInvitable?.shiftId) {
    query.$modify = {
      ...query.$modify,
      onlyInvitable: [queryConfig.onlyInvitable.shiftId],
    };
  }

  if (queryConfig.exclude?.personIds) {
    query.$and.push(
      {
        'person.id': { $nin: queryConfig.exclude.personIds },
      },
    );
  }

  if (excludeIds || queryConfig.exclude?.personIds) {
    query.$and.push(
      {
        'person.id': { $nin: [...excludeIds, ...(queryConfig.exclude?.personIds ?? [])] },
      },
    );
  }
  return query;
};

export const fetchData = async (query: Query) => feathersClient
  .service('person').find({ query }).catch(() => {
    ErrorAlert('Nastala chyba');
    return { data: [], total: 0 };
  });

// Mapping with categories simplifies work with categories in the select options.
// see TySelectInvitationOption.tsx
const mapWithCategory = (data: PersonData[], category: PersonCategory) => data.map((item) => ({ ...item, category }));

export const loadPersonOptions =
    // queryConfig is set of data obtained from TySelectInvitations.tsx component's props.
    (queryConfig: TySelectInvitationQueryConfig) =>
    // search and options are params used by AsyncSelect lib.
      async (
        search: string,
        options: OptionsOrGroups<PersonData, GroupBase<PersonData>>,
      ) => {
        // If the user is searching, skip any other fetch requests for categories (e.g. isNewbieForBranchOffice)
        // and only use this result.
        if (search) {
          const searchQuery = queryBuilder(queryConfig, {}, '[application(latestOneWithOkAttendance).[shift], personData(attendanceRate), blockedCompany, activeBan]', [], 10, search);
          const searchData = (await fetchData(searchQuery)) as Paginated<PersonData>;

          return {
            options: [
              ...mapWithCategory(searchData.data, PersonCategory.Others),
            ],
            hasMore: searchData.total > options.length + searchData.data.length,
          };
        }

        // Categories in which we want to fetch users are described here.
        // https://3.basecamp.com/5751779/buckets/36326941/card_tables/cards/7971384242

        // Hold an array of already categorized users so they does not appear in other categories.
        const excludedIds: number[] = [];

        const branchOfficeId = queryConfig.branchOfficeId ?? 0;

        if (branchOfficeId === 0) {
          return {
            options: [],
          };
        }

        // Category 1.
        let newbiesForBranchOffice: Paginated<PersonData> = { data: [], total: 0, limit: 0, skip: 0 };
        if (queryConfig.branchOfficeId) {
          const newbiesForBranchOfficeQuery = queryBuilder(queryConfig, { isNewbieForBranchOffice: [queryConfig.branchOfficeId], hasPerks: [queryConfig.shiftPerks] }, '[application(latestOneWithOkAttendance).[shift], personData(attendanceRate), blockedCompany, activeBan]', [], undefined);
          newbiesForBranchOffice = (await fetchData(newbiesForBranchOfficeQuery)) as Paginated<PersonData>;
          excludedIds.push(...newbiesForBranchOffice.data.map((item) => item.id));
        }

        // Category 2.
        const bestRatedQuery = queryBuilder(queryConfig, { hasPositiveJobEvaluationInPastMonth: [branchOfficeId], hasPerks: [queryConfig.shiftPerks] }, '[personData(attendanceRate), blockedCompany, activeBan]', [...new Set(excludedIds)], undefined);
        const bestRated = (await fetchData(bestRatedQuery)) as Paginated<PersonData>;
        excludedIds.push(...bestRated.data.map((item) => item.id));

        // Category 3.
        const mostAttendancesInMonthQuery = queryBuilder(queryConfig, { hasMostAttendancesInPastWeeks: [branchOfficeId, 4], hasPerks: [queryConfig.shiftPerks] }, '[application(inPastMonthWithOkAttendance), personData(attendanceRate), blockedCompany, activeBan]', [...new Set(excludedIds)], undefined);
        const mostAttendancesInMonth = (await fetchData(mostAttendancesInMonthQuery)) as Paginated<PersonData>;
        excludedIds.push(...mostAttendancesInMonth.data.map((item) => item.id));

        // Category 4. -- commented out now for performance reasons, 52 weeks takes about 2 to 4 seconds to load
        // const mostAttendancesInYearQuery = queryBuilder(queryConfig, { hasMostAttendancesInPastWeeks: [branchOfficeId, 52], hasPerks: [queryConfig.shiftPerks] }, '[application(inPastYearWithOkAttendance), personData(attendanceRate), blockedCompany]', [...new Set(excludedIds)], undefined);
        // const mostAttendancesInYear = (await fetchData(mostAttendancesInYearQuery)) as Paginated<PersonData>;

        // Return all the categories in order as options to the select.
        return {
          options: [
            ...mapWithCategory(newbiesForBranchOffice.data.filter(user => user.activeBan === null), PersonCategory.Newbie),
            ...mapWithCategory(bestRated.data.filter(user => user.activeBan === null), PersonCategory.BestRated),
            ...mapWithCategory(mostAttendancesInMonth.data.filter(user => user.activeBan === null), PersonCategory.MostAttendancesInMonth),
            // ...mapWithCategory(mostAttendancesInYear.data, PersonCategory.MostAttendancesInYear),
          ],
        };
      };

// Label serves as additional info for each category ("In past month Xx on your shift" or "Is banned on this company")
export const renderOptionLabel = (
  option: TySelectInvitationOptionData,
  selectedCompany: CompanyProp,
): JSX.Element | null => {
  const blocks = getBlocks(option.blockedCompany, selectedCompany);
  let props = option;
  if ((isBlocked(blocks) || option.activeBan !== null) && option.category === PersonCategory.Others) {
    props = { ...option, category: PersonCategory.Blocked, blocks };
  } else if (isBlocked(blocks) && option.category !== PersonCategory.Others) {
    return <></>;
  }
  return <TySelectInvitationOption option={props} />;
};

export const attendanceRateCalculation = (att?: number, app?: number) => {
  if (att && app && app > 0) {
    return Math.min(100 * (att / app), 100);
  }
  return false;
};

// Just Czech things...
export const getCzechConjugationForShiftNoun = (count: number) => {
  let suffix;
  if (count === 1) {
    suffix = 'směna';
  } else if (count >= 2 && count <= 4 && !(count % 10 === 2 || count % 10 === 3 || count % 10 === 4)) {
    suffix = 'směny';
  } else {
    suffix = 'směn';
  }
  return suffix;
};
